import {useCallback, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ModelPickerDesign} from '@github-ui/copilot-chat/components/ModelPicker'
import {useAgentModels} from '../../hooks/useAgentModels'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotChatModel} from '@github-ui/copilot-chat/utils/copilot-chat-types'

const STORAGE_KEY = 'AGENT_TASKS_SELECTED_MODEL'

interface ModelPickerProps {
  onUpdateModel: (model: string) => void
  selectedModelName?: string
}

function createPlaceholderModel(invalidModelId: string): CopilotChatModel {
  return {
    id: invalidModelId,
    name: `${invalidModelId} (unavailable)`,
    displayName: `${invalidModelId} (unavailable)`,
    hasLimitedCapabilities: true,
    isThirdParty: false,
    version: '',
    vendor: '',
    preview: false,
    model_picker_enabled: false,
    capabilities: {
      type: 'chat',
      family: '',
      limits: {max_prompt_tokens: 0},
      supports: {},
      tokenizer: '',
    },
  }
}

export function ModelPicker({onUpdateModel, selectedModelName}: ModelPickerProps) {
  const {models: availableModels, isLoading} = useAgentModels()

  const [internalSelectedModel, setInternalSelectedModel] = useState<string>(() => {
    if (typeof window !== 'undefined' && !selectedModelName) {
      return localStorage.getItem(STORAGE_KEY) || ''
    }
    return selectedModelName || ''
  })

  const currentSelectedModel = selectedModelName ?? internalSelectedModel

  const handleUpdateModel = useCallback(
    (model: CopilotChatModel) => {
      const modelId = model.id
      setInternalSelectedModel(modelId)
      localStorage.setItem(STORAGE_KEY, modelId)
      onUpdateModel(modelId)
    },
    [onUpdateModel],
  )

  const foundModel = availableModels?.find(model => model.id === currentSelectedModel)
  const defaultModel = availableModels?.find(m => m.is_chat_default) ?? availableModels?.[0]

  // If no model selected yet, use default
  const selectedModel = currentSelectedModel ? foundModel ?? createPlaceholderModel(currentSelectedModel) : defaultModel

  if (isLoading || !availableModels || !selectedModel) return null

  return (
    <ModelPickerDesign
      models={availableModels}
      selectedModel={selectedModel}
      onModelPicked={handleUpdateModel}
      disabled={false}
      type={'global'}
      variant={'invisible'}
      showCategories={false}
      showPreviewModelsSeparately={false}
    />
  )
}

try{ ModelPicker.displayName ||= 'ModelPicker' } catch {}